import React from "react";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Compact pagination control. Pure presentational.
 */
export default function Pagination({ page, pageSize, total, onPage }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between gap-2 pt-3" data-testid="pagination">
      <div className="text-xs text-muted-foreground tabular-nums">
        {from}–{to} of {total.toLocaleString("en-IE")}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline" size="sm" disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="rounded-lg h-8 w-8 p-0"
          data-testid="pagination-prev"
        ><ChevronLeft className="w-4 h-4" /></Button>
        <div className="text-xs font-mono px-2 tabular-nums">
          {page} / {pages}
        </div>
        <Button
          variant="outline" size="sm" disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          className="rounded-lg h-8 w-8 p-0"
          data-testid="pagination-next"
        ><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

export const StatusBadge = ({ status }) => {
  const map = {
    completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    cancelled: "bg-rose-100 text-rose-700 border-rose-200",
    awaiting_payment: "bg-amber-100 text-amber-700 border-amber-200",
    sourcing_liquidity: "bg-sky-100 text-sky-700 border-sky-200",
    sending_crypto: "bg-sky-100 text-sky-700 border-sky-200",
    on_chain_confirmed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    payment_received: "bg-sky-100 text-sky-700 border-sky-200",
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-100 text-rose-700 border-rose-200",
    matched: "bg-emerald-100 text-emerald-700 border-emerald-200",
    unmatched: "bg-amber-100 text-amber-700 border-amber-200",
  };
  const cls = map[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${cls}`}>
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
};

export const KvRow = ({ label, value, mono = false }) => (
  <div className="flex justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
    <span className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">{label}</span>
    <span className={`text-foreground font-semibold text-right ${mono ? "font-mono text-xs" : ""}`}>
      {value ?? "—"}
    </span>
  </div>
);

export const ReasonPrompt = ({ open, onOpenChange, title, description, onConfirm, confirmLabel = "Confirm", danger = false }) => {
  const [reason, setReason] = React.useState("");
  React.useEffect(() => { if (open) setReason(""); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 backdrop-blur-sm" onClick={() => onOpenChange(false)} data-testid="reason-prompt">
      <div className="bg-white rounded-3xl p-6 w-[440px] max-w-[92vw] space-y-3 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-lg font-bold text-foreground">{title}</h3>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
        <textarea
          value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (required)…"
          rows={3}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-primary"
          data-testid="reason-input"
          autoFocus
        />
        <div className="flex gap-2 justify-end pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl" data-testid="reason-cancel">Cancel</Button>
          <Button
            disabled={!reason.trim()}
            onClick={() => { onConfirm(reason.trim()); onOpenChange(false); }}
            className={`rounded-xl ${danger ? "bg-destructive hover:bg-destructive/90" : "bg-primary hover:bg-primary/90"} text-white`}
            data-testid="reason-confirm"
          >{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
};
